import React, { useCallback, useEffect, useState, useMemo } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import axios from 'axios';

import { useTranslation } from 'react-i18next';
import { Box, Button, Divider, Fab, Tab, Tabs, Typography } from '@mui/material';
import useImage from 'services/Hooks/useImage';
import { useParams } from 'react-router';
import { useInventoryItem } from 'services/Data/InventoryItemServices';
import GridContainer from '@jumbo/components/GridContainer';
import GridItem from '@jumbo/components/GridItem';
import CmtCard from '@coremat/CmtCard';
import CmtCardContent from '@coremat/CmtCard/CmtCardContent';
import { Add, AttachFile, Remove } from '@mui/icons-material';
import ImagesSlideView from 'Components/ImagesSlideView';
import SkeletonWrapper from 'Components/SkeletonWrapper';
import DimensionTypeEnum from 'enums/DimensionTypeEnum';
import { useLanguageReference } from 'services/Hooks/useLanguageReference';
import { v4 as uuidv4 } from 'uuid';
import { useActiveCart, usePostBrpClubCartItem, usePostCarItem } from 'services/Data/CartService';
import FrequentlyBoughtTogetherTypeEnum from 'enums/FrequentlyBoughtTogetherTypeEnum';
import FrequentlyBoughtTogether from '../Components/FrequentlyBoughtTogether';
import { useSelector } from 'react-redux';
import { FileTypeEnumValue } from 'enums/FileTypeEnum';
import { TabContext, TabPanel } from '@mui/lab';
import BaseModal from 'Components/BaseModal';
import CartRedirectionModal from '../Components/CartRedirectionModal';
import { IsClub, IsImpersonationMode } from 'config/config';

const useStyles = makeStyles(theme => ({
  description: {
    '& ul': {
      marginLeft: '2vh',
    },
  },
  tabsList: {
    position: 'relative',
    minHeight: 10,
    '& .MuiTabs-indicator': {
      backgroundColor: theme.palette.secondary.main,
    },
    '& :hover': {
      color: theme.palette.secondary.main,

      '& >svg': {
        color: theme.palette.secondary.main,
      },
    },
    '& .Mui-selected': {
      color: `${theme.palette.secondary.main}`,
    },
  },
  tabItem: {
    maxWidth: 'none',
    minWidth: 10,
    minHeight: 10,
    paddingLeft: 20,
    paddingRight: 20,
    textTransform: 'none',
    color: theme.palette.common.black,
    fontSize: 18,
    fontWeight: theme.typography.fontWeightRegular,
  },
  fileLink: {
    color: theme.palette.common.black,
    cursor: 'pointer',
    '&:hover': {
      color: theme.palette.secondary.main,
    },
  },
}));

const InventoryItemCategory = ({ inventoryItemIdOverride, isQuickShop = false, onQuickShopSuccess } = {}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const locale = t('locale');

  const { inventoryItemId: legacyInventoryItemId, dealerPortalMenuProductId } = useParams();
  const inventoryItemId = inventoryItemIdOverride || legacyInventoryItemId || dealerPortalMenuProductId;
  const accountReducer = useSelector(state => state.AccountReducer);
  const dealerId = accountReducer?.profile?.dealer_Id;

  const [currentTabValue, setCurrentTabValue] = useState(1);
  const [showRedirectionModal, setShowRedirectionModal] = useState(false);

  const { data: cart } = useActiveCart(accountReducer.isLoggedIn, false, IsImpersonationMode ? dealerId : null);
  const { labelReference, nameReference } = useLanguageReference();
  const { getInventoryItemImage, getInventoryItemVariantImage } = useImage();
  const { data: inventoryItem, isLoading: gettingInventoryItem } = useInventoryItem(inventoryItemId, true);

  const [quantity, setQuantity] = useState(1);
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(null);
  const [itemToReplace, setItemToReplace] = useState(null);
  const { mutate: addCartItem } = usePostCarItem(accountReducer.isDemo);
  const { mutate: addBrpClubCartItem } = usePostBrpClubCartItem(false);

  useEffect(() => {
    if (!isQuickShop) {
      window.scroll({
        top: 0,
        behavior: 'smooth',
      });
    }
  }, [isQuickShop]);

  const existingCartItem = useMemo(() => {
    if (!inventoryItem || !cart?.cartItems) return null;
    return cart.cartItems.find(ci => ci.inventoryItem_Id === inventoryItem.id);
  }, [cart, inventoryItem]);

  const quantityInCart = existingCartItem?.quantity ?? 0;

  const hasPromo = inventoryItem?.isInPromotion;
  const inventoriedQuantityAvailable = inventoryItem?.inventoriedQuantityAvailable;
  const canOfferReplacement =
    inventoriedQuantityAvailable != null &&
    quantityInCart >= inventoriedQuantityAvailable &&
    Boolean(inventoryItem?.inventoryItemOnceDepleted_Id);

  const remainingPromotionQuantity = hasPromo ? inventoryItem.promotionQuantity - quantityInCart : Infinity;
  const remainingInventoriedQuantity =
    inventoriedQuantityAvailable != null ? inventoriedQuantityAvailable - quantityInCart : Infinity;
  const maxAvailableQuantity = Math.min(remainingPromotionQuantity, remainingInventoriedQuantity);

  const safeMaxAvailableQuantity = Math.max(1, maxAvailableQuantity);
  const isMaxQuantityReached = quantity >= safeMaxAvailableQuantity;
  const variants = (inventoryItem?.inventoryItemVariants ?? []).filter(variant => variant?.isActive);
  const variantsCount = variants.length;
  const hasVariants = variantsCount > 0;
  const selectedVariant = hasVariants && selectedVariantIndex !== null ? variants[selectedVariantIndex] : null;

  const variantDescription =
    selectedVariant &&
    (locale === 'fr'
      ? selectedVariant?.variantDescriptionFr ?? selectedVariant?.variantDescriptionEn
      : selectedVariant?.variantDescriptionEn ?? selectedVariant?.variantDescriptionFr);
  const resolvedShortDescription = variantDescription || inventoryItem?.shortDescription;
  const isPodLinkedBundledItem = item =>
    item?.linkedToPod === true ||
    item?.linkedToPOD === true ||
    item?.LinkedToPOD === true ||
    item?.isLinkedToPOD === true ||
    item?.IsLinkedToPOD === true;
  const bundledItemsToDisplay = (inventoryItem?.bundledInventoryItems ?? []).filter(item => !isPodLinkedBundledItem(item));

  const podLinkedBundledPrice = useMemo(
    () =>
      (inventoryItem?.bundledInventoryItems ?? [])
        .filter(isPodLinkedBundledItem)
        .reduce((total, item) => total + Number(item?.price ?? 0), 0),
    [inventoryItem?.bundledInventoryItems],
  );

  const podLinkedBundledPromotionPrice = useMemo(
    () =>
      (inventoryItem?.bundledInventoryItems ?? [])
        .filter(isPodLinkedBundledItem)
        .reduce((total, item) => total + Number(item?.promotionPrice ?? 0), 0),
    [inventoryItem?.bundledInventoryItems],
  );

  const basePricing = useMemo(
    () => ({
      price: Math.max(0, Number(inventoryItem?.price ?? 0) - podLinkedBundledPrice),
      promotionPrice: Math.max(0, Number(inventoryItem?.promotionPrice ?? 0) - podLinkedBundledPromotionPrice),
      currencyName: inventoryItem?.currencyName ?? '',
      quantityPerPackage: inventoryItem?.quantityPerPackage ?? 0,
    }),
    [
      inventoryItem?.currencyName,
      inventoryItem?.price,
      inventoryItem?.promotionPrice,
      inventoryItem?.quantityPerPackage,
      podLinkedBundledPrice,
      podLinkedBundledPromotionPrice,
    ],
  );

  const variantPricing = useMemo(() => {
    if (!selectedVariant) return null;
    const hasOwnPriceValue = selectedVariant?.price !== undefined && selectedVariant?.price !== null;
    if (!hasOwnPriceValue) {
      return null;
    }

    const parsedPrice = Number(selectedVariant?.price);
    if (Number.isNaN(parsedPrice)) {
      return null;
    }

    const parsedPromotion = Number(selectedVariant?.promotionPrice ?? 0);

    return {
      price: parsedPrice,
      promotionPrice: Number.isNaN(parsedPromotion) ? 0 : parsedPromotion,
      currencyName: selectedVariant?.currencyName ?? basePricing.currencyName,
      quantityPerPackage: selectedVariant?.quantityPerPackage ?? basePricing.quantityPerPackage,
    };
  }, [basePricing.currencyName, basePricing.quantityPerPackage, selectedVariant]);

  const resolvedPricing = variantPricing ?? basePricing;
  const hasPromotionPrice = Number(resolvedPricing?.promotionPrice ?? 0) > 0;
  const formatCurrencyValue = value => Number(value ?? 0).toFixed(2);
  const resolvedCurrencyName = resolvedPricing?.currencyName || '';
  const resolvedQuantityPerPackage = resolvedPricing?.quantityPerPackage ?? 0;

  const inventoryItemImages = useMemo(() => {
    return inventoryItem?.images?.filter(image => image.fileType === FileTypeEnumValue.Photo) ?? [];
  }, [inventoryItem?.images]);

  const variantImages = useMemo(() => {
    if (!selectedVariant?.files?.length) return [];
    return selectedVariant.files
      .filter(file => {
        if (file?.contentType) {
          return file.contentType.toLowerCase().startsWith('image');
        }
        if (typeof file?.fileType === 'number') {
          return file.fileType === FileTypeEnumValue.Photo || file.fileType === FileTypeEnumValue.None;
        }
        return true;
      })
      .map(file => {
        const resolvedFileId =
          file?.id ?? file?.fileId ?? file?.file_Id ?? file?.attachedFileId ?? file?.attachedFile_Id ?? null;

        return resolvedFileId ? { ...file, id: resolvedFileId } : null;
      })
      .filter(Boolean);
  }, [selectedVariant?.files]);

  const displayImages = variantImages.length > 0 ? variantImages : inventoryItemImages;
  useEffect(() => {
    if (!hasVariants && selectedVariantIndex !== null) {
      setSelectedVariantIndex(null);
    } else if (hasVariants && selectedVariantIndex !== null && selectedVariantIndex >= variantsCount) {
      setSelectedVariantIndex(null);
    }
  }, [hasVariants, selectedVariantIndex, variantsCount]);

  const handleAddToCart = useCallback(
    inventoryItem => {
      if (canOfferReplacement) {
        setItemToReplace(inventoryItem);
        return;
      }

      const variantToAdd =
        selectedVariantIndex !== null && variants.length > selectedVariantIndex ? variants[selectedVariantIndex] : null;
      const resolvedUnitPrice = Number(resolvedPricing?.price ?? inventoryItem?.price ?? 0);
      const resolvedPromotionPrice = hasPromotionPrice
        ? Number(resolvedPricing?.promotionPrice ?? resolvedUnitPrice)
        : resolvedUnitPrice;
      const resolvedCurrency = resolvedPricing?.currencyName || inventoryItem?.currencyName;
      const cartItem = {
        id: uuidv4(),
        inventoryItem_Id: inventoryItem.id,
        quantity: quantity,
        unitPrice: resolvedUnitPrice,
        currencyName: resolvedCurrency,
        cart_Id: cart?.id,
        unitPricePromotion: resolvedPromotionPrice,
        promotionQuantity: inventoryItem.promotionQuantity,
        isInPromotion: inventoryItem.isInPromotion,
        imgSrc: inventoryItem?.images.find(i => i.dealerPortalPrimary)
          ? inventoryItem?.images.find(i => i.dealerPortalPrimary)
          : inventoryItem?.images[0],
        name: inventoryItem.name,
        InventoryItemVariant_Id: variantToAdd?.id,
      };

      if (IsClub) {
        addBrpClubCartItem(cartItem);
      } else {
        addCartItem(cartItem);
      }

      if (isQuickShop) {
        if (onQuickShopSuccess) {
          onQuickShopSuccess();
        }
      } else {
        setShowRedirectionModal(true);
      }
    },
    [
      addBrpClubCartItem,
      addCartItem,
      canOfferReplacement,
      cart?.id,
      hasPromotionPrice,
      isQuickShop,
      onQuickShopSuccess,
      quantity,
      resolvedPricing,
      selectedVariantIndex,
      variants,
    ],
  );

  const handleCloseReplacementModal = useCallback(() => {
    setItemToReplace(null);
  }, []);

  const handleConfirmReplacementAdd = useCallback(() => {
    if (!itemToReplace?.inventoryItemOnceDepleted_Id) {
      return;
    }

    const replacementCartItem = {
      id: uuidv4(),
      inventoryItem_Id: itemToReplace.inventoryItemOnceDepleted_Id,
      quantity,
      cart_Id: cart?.id,
    };

    if (IsClub) {
      addBrpClubCartItem(replacementCartItem);
    } else {
      addCartItem(replacementCartItem);
    }

    setItemToReplace(null);

    if (isQuickShop) {
      if (onQuickShopSuccess) {
        onQuickShopSuccess();
      }
    } else {
      setShowRedirectionModal(true);
    }
  }, [addBrpClubCartItem, addCartItem, cart?.id, isQuickShop, itemToReplace, onQuickShopSuccess, quantity]);

  const handleDownloadFile = useCallback((fileUrl, fileName) => {
    axios
      .get(fileUrl, {
        responseType: 'blob',
        headers: {
          'Content-Disposition': 'attachment',
        },
      })
      .then(response => {
        const href = URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = href;
        var filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        var matches = filenameRegex.exec(response.headers['content-disposition']);
        if (matches != null && matches[1]) {
          const originalFileName = matches[1].replace(/['"]/g, '');
          const extension = originalFileName.split('.')[1];

          link.setAttribute('download', `${fileName}.${extension}`);
        }
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(href);
      });
  }, []);

  const handleVariantSelection = useCallback(index => {
    setSelectedVariantIndex(index);
  }, []);

  const resolveImageUrl = useCallback(
    imageId => {
      const matchedVariantImage = variantImages.find(image => String(image?.id) === String(imageId));
      if (matchedVariantImage) {
        const variantImageOwnerId =
          matchedVariantImage?.inventoryItemVariant_Id ?? matchedVariantImage?.inventoryItemVariantId ?? selectedVariant?.id;

        if (variantImageOwnerId) {
          return getInventoryItemVariantImage(variantImageOwnerId, matchedVariantImage.id);
        }
      }

      return getInventoryItemImage(inventoryItem?.id, imageId);
    },
    [getInventoryItemImage, getInventoryItemVariantImage, inventoryItem?.id, selectedVariant?.id, variantImages],
  );
  if (!inventoryItemId) return null;
  return (
    <Box sx={{ mt: 2 }}>
      <BaseModal
        show={!!itemToReplace}
        title={t('replaceItem')}
        message={t('inventoriedItemReplacementOfferText', {
          currentItem: itemToReplace?.name || itemToReplace?.sku || '',
        })}
        closeBtnMessage={t('close')}
        onClose={handleCloseReplacementModal}
        actions={[
          {
            color: 'secondary',
            variant: 'contained',
            onClick: handleConfirmReplacementAdd,
            message: t('replaceItem'),
          },
        ]}
      />
      <CartRedirectionModal
        show={showRedirectionModal}
        onClose={() => setShowRedirectionModal(false)}
        redirectLink={'/dealer/cart'}
        title={t('itemAddedToCart')}
        message={t('itemAddedToCartText')}
        cartId={cart?.id}
        inventoryItemId={inventoryItemId}
        currencyName={resolvedPricing?.currencyName}
        inventoryItemData={inventoryItem}
      />

      <GridContainer>
        <GridItem xs={12} xl={8} lg={7} md={6}>
          <ImagesSlideView
            key={selectedVariantIndex === null ? 'base-item' : `variant-${selectedVariantIndex}`}
            isLoading={gettingInventoryItem}
            images={displayImages}
            getImageUrl={resolveImageUrl}
            enableMagnifier
          />
        </GridItem>
        <GridItem xs={12} xl={4} lg={5} md={6}>
          <CmtCard>
            <CmtCardContent>
              <Box display="flex" flexDirection={'column'} sx={{ mb: 2 }}>
                <SkeletonWrapper
                  skeletonProps={{
                    width: 210,
                    height: 33,
                  }}
                  isLoading={gettingInventoryItem}
                  component={<Typography variant="h1">{inventoryItem?.name}</Typography>}
                />
                <SkeletonWrapper
                  skeletonProps={{
                    width: 210,
                    height: 30,
                  }}
                  isLoading={gettingInventoryItem}
                  component={
                    <Box display="flex" alignItems="center" gap={2} sx={{ mt: 1, mb: 0.5 }}>
                      {selectedVariant && (
                        <Box display="flex" alignItems="center" color="text.secondary" sx={{ gap: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            {t('selectedVariantLabel', { defaultValue: 'Selected variant' })}{' '}
                          </Typography>
                          <Typography variant="h5" color="text.secondary" textTransform="none">
                            {(locale === 'fr'
                              ? selectedVariant?.variantNameFr ?? selectedVariant?.variantNameEn
                              : selectedVariant?.variantNameEn ?? selectedVariant?.variantNameFr) ||
                              t('variant', { defaultValue: 'Variant' })}
                          </Typography>
                        </Box>
                      )}
                      <Typography variant="h5" color="text.secondary">
                        {selectedVariant?.variantSku ?? inventoryItem?.sku}
                      </Typography>
                    </Box>
                  }
                />
                <Divider />
              </Box>
              <Box display="flex" flexDirection={'column'}>
                <SkeletonWrapper
                  skeletonProps={{
                    width: 210,
                    height: 60,
                  }}
                  isLoading={gettingInventoryItem}
                  component={
                    <div>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}>
                        <Box>
                          <Typography variant="h2">{t('price')}</Typography>
                        </Box>
                        <Box>
                          {hasPromotionPrice ? (
                            <>
                              <Typography fontSize={16} color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                                ${formatCurrencyValue(resolvedPricing?.price)} {resolvedCurrencyName}
                              </Typography>
                              <Typography fontSize={25} color="error" fontWeight="bold">
                                ${formatCurrencyValue(resolvedPricing?.promotionPrice)} {resolvedCurrencyName}
                              </Typography>
                            </>
                          ) : (
                            <Typography fontSize={25} fontWeight="bold">
                              ${formatCurrencyValue(resolvedPricing?.price)} {resolvedCurrencyName}
                              {resolvedQuantityPerPackage > 0 && (
                                <Typography sx={{ paddingLeft: '0.5rem' }} component="strong" color="text.secondary">
                                  ({t('quantityPerPackageInfo', { quantityPerPackage: resolvedQuantityPerPackage })})
                                </Typography>
                              )}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      {hasVariants && (
                        <Box sx={{ mt: 3 }}>
                          <Typography variant="h5" sx={{ mb: 2 }}>
                            {t('variants', { defaultValue: 'Variants' })}
                          </Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: 2,
                            }}>
                            <Box
                              key="variant-default-option"
                              sx={{
                                borderRadius: 1,
                                border: theme =>
                                  `2px solid ${
                                    selectedVariantIndex === null ? theme.palette.secondary.main : theme.palette.divider
                                  }`,
                                minWidth: 140,
                                p: 2,
                                backgroundColor: theme => theme.palette.background.paper,
                                boxShadow: theme => (selectedVariantIndex === null ? theme.shadows[4] : theme.shadows[1]),
                                cursor: 'pointer',
                              }}
                              role="button"
                              tabIndex={0}
                              aria-pressed={selectedVariantIndex === null}
                              onClick={() => handleVariantSelection(null)}
                              onKeyDown={event => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                  event.preventDefault();
                                  handleVariantSelection(null);
                                }
                              }}>
                              <Typography fontWeight={600} fontSize={15} sx={{ textTransform: 'capitalize' }}>
                                {t('variantDefaultOption', { defaultValue: 'Base item' })}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {inventoryItem?.sku}
                              </Typography>
                            </Box>
                            {variants.map((variant, index) => {
                              const isSelected = index === selectedVariantIndex;

                              return (
                                <Box
                                  key={variant?.id ?? variant?.variantSku ?? `${variant?.variantName || 'variant'}-${index}`}
                                  sx={{
                                    borderRadius: 1,
                                    border: theme =>
                                      `2px solid ${isSelected ? theme.palette.secondary.main : theme.palette.divider}`,
                                    minWidth: 140,
                                    p: 2,
                                    backgroundColor: theme => theme.palette.background.paper,
                                    boxShadow: theme => (isSelected ? theme.shadows[4] : theme.shadows[1]),
                                    cursor: 'pointer',
                                  }}
                                  role="button"
                                  tabIndex={0}
                                  aria-pressed={isSelected}
                                  onClick={() => handleVariantSelection(index)}
                                  onKeyDown={event => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                      event.preventDefault();
                                      handleVariantSelection(index);
                                    }
                                  }}>
                                  <Typography fontWeight={600} fontSize={15} sx={{ textTransform: 'capitalize' }}>
                                    {(locale === 'fr'
                                      ? variant?.variantNameFr ?? variant?.variantNameEn
                                      : variant?.variantNameEn ?? variant?.variantNameFr) ||
                                      t('variant', { defaultValue: 'Variant' })}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {variant?.variantSku || t('skuUnavailable', { defaultValue: 'SKU unavailable' })}
                                  </Typography>
                                </Box>
                              );
                            })}
                          </Box>
                        </Box>
                      )}
                      {resolvedShortDescription && (
                        <Box>
                          <Divider sx={{ mt: 2, mb: 4 }} />
                          <Typography fontSize={14} justifyContent={'center'}>
                            {resolvedShortDescription}
                          </Typography>
                        </Box>
                      )}
                      {bundledItemsToDisplay.length > 0 && (
                        <Box>
                          <Divider sx={{ mt: resolvedShortDescription ? 4 : 2, mb: 4 }} />
                          <Typography variant="h5">{t('hasBundledItems')}</Typography>
                        </Box>
                      )}

                      {bundledItemsToDisplay.length === 0 && !resolvedShortDescription && (
                        <Box>
                          <Divider sx={{ mt: resolvedShortDescription ? 4 : 2, mb: 4 }} />
                        </Box>
                      )}

                      <Box sx={{ ml: 4 }}>
                        {bundledItemsToDisplay.map(item => (
                          <ul key={item?.id ?? item?.inventoryItem_Id ?? item?.sku ?? item?.name}>
                            <li>
                              <Typography fontSize={12} justifyContent={'center'} sx={{ mb: 1 }}>
                                {item.name || item.sku}
                              </Typography>
                            </li>
                          </ul>
                        ))}
                      </Box>
                      {bundledItemsToDisplay.length > 0 && <Divider sx={{ mt: 4, mb: 4 }} />}
                    </div>
                  }
                />
                <Box
                  sx={{
                    pt: 0,
                    display: 'flex',
                    flexDirection: isQuickShop ? 'column' : 'row',
                    justifyContent: isQuickShop ? 'flex-start' : 'space-between',
                    alignItems: isQuickShop ? 'stretch' : 'center',
                    gap: isQuickShop ? 2 : 0,
                  }}>
                  <Box
                    fontSize={20}
                    sx={{
                      width: isQuickShop ? '100%' : '15vh',
                      mt: 0,
                      mb: 0,
                    }}
                    display="flex"
                    justifyContent={'space-between'}
                    alignItems={'center'}>
                    <Fab
                      size="small"
                      color={'secondary'}
                      style={{ borderRadius: 0, width: 36, height: 36, minHeight: 36, color: '#000' }}
                      disabled={quantity <= 1}
                      onClick={() => setQuantity(q => Math.max(1, q - 1))}>
                      <Remove />
                    </Fab>
                    <Typography>{quantity}</Typography>
                    <Fab
                      size="small"
                      color={'secondary'}
                      style={{ borderRadius: 0, width: 36, height: 36, minHeight: 36, color: '#000' }}
                      disabled={
                        (hasPromo && isMaxQuantityReached) ||
                        (inventoriedQuantityAvailable != null && isMaxQuantityReached && !canOfferReplacement)
                      }
                      onClick={() => {
                        if (inventoriedQuantityAvailable != null && isMaxQuantityReached && canOfferReplacement) {
                          setItemToReplace(inventoryItem);
                        } else if (hasPromo || inventoriedQuantityAvailable != null) {
                          setQuantity(q => Math.min(safeMaxAvailableQuantity, q + 1));
                        } else {
                          setQuantity(q => q + 1);
                        }
                      }}>
                      <Add />
                    </Fab>
                  </Box>
                  <Box sx={{ width: isQuickShop ? '100%' : 'auto' }}>
                    <Button
                      variant={'contained'}
                      color={'secondary'}
                      sx={{
                        borderRadius: 0,
                        color: '#000',
                        fontWeight: 800,
                        fontSize: 14,
                        boxShadow: 'none',
                        '&:hover': {
                          backgroundColor: '#EFBA29',
                          boxShadow: 'none',
                        },
                        mt: isQuickShop ? 1 : 0,
                      }}
                      fullWidth={isQuickShop}
                      onClick={() => handleAddToCart(inventoryItem)}
                      disabled={
                        (hasPromo && quantityInCart + quantity > inventoryItem.promotionQuantity) ||
                        (inventoriedQuantityAvailable != null &&
                          quantityInCart + quantity > inventoriedQuantityAvailable &&
                          !canOfferReplacement)
                      }>
                      <Typography
                        sx={{
                          color: 'black !important',
                          fontSize: 22,
                          fontWeight: 800,
                          paddingRight: 0,
                          paddingLeft: 0,
                          paddingTop: 2,
                          paddingBottom: 2,
                          fontFamily: 'Trade Gothic Next LT Pro',
                          letterSpacing: '-0.02em',
                          mb: 1,
                        }}>
                        {t('addToCart')}
                      </Typography>
                    </Button>
                    {inventoriedQuantityAvailable != null && (
                      <Typography
                        fontSize={12}
                        color="text.secondary"
                        sx={{ mt: 1, textAlign: isQuickShop ? 'left' : 'right' }}>
                        {t('inventoriedQuantityLeft', { count: inventoriedQuantityAvailable })}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            </CmtCardContent>
          </CmtCard>
          <CmtCard sx={{ mt: 2, minHeight: '20vh' }}>
            <CmtCardContent style={{ height: '100%' }}>
              <Box sx={{ height: '100%' }}>
                <TabContext value={currentTabValue}>
                  <Tabs
                    className={classes.tabsList}
                    value={currentTabValue}
                    onChange={(_, value) => {
                      setCurrentTabValue(value);
                    }}
                    sx={{ height: '100%' }}
                    aria-label="profile tabs"
                    variant="scrollable"
                    allowScrollButtonsMobile>
                    <Tab className={classes.tabItem} value={1} label={t('overview')} />
                    <Tab className={classes.tabItem} value={2} label={t('installationFiles')} />
                  </Tabs>

                  <TabPanel
                    value={1}
                    sx={{
                      height: '100%',
                    }}>
                    <SkeletonWrapper
                      skeletonProps={{
                        height: 36,
                      }}
                      fullWidth
                      isLoading={gettingInventoryItem}
                      component={
                        <Typography>
                          <span
                            className={classes.description}
                            dangerouslySetInnerHTML={{
                              __html: inventoryItem?.description,
                            }}
                          />
                        </Typography>
                      }
                    />
                    <Box sx={{ pt: 2, pb: 2 }}>
                      <Divider />
                    </Box>
                    <SkeletonWrapper
                      skeletonProps={{
                        height: 36,
                      }}
                      fullWidth
                      isLoading={gettingInventoryItem}
                      component={
                        inventoryItem?.itemHeight || inventoryItem?.itemWidth || inventoryItem?.itemDepth ? (
                          <Typography>{`${t('dimension')}: ${t('inventoryItemDimension', {
                            width: inventoryItem?.itemWidth || 0,
                            height: inventoryItem?.itemHeight || 0,
                            depth: inventoryItem?.itemDepth || 0,
                          })}
                      ${DimensionTypeEnum.find(cur => cur?.value === inventoryItem.itemDimensionTypeEnum)?.[
                        labelReference
                      ] || ''}`}</Typography>
                        ) : (
                          <Typography>{`${t('dimension')}: N/A`}</Typography>
                        )
                      }
                    />
                    <SkeletonWrapper
                      skeletonProps={{
                        height: 36,
                      }}
                      fullWidth
                      isLoading={gettingInventoryItem}
                      component={
                        inventoryItem?.itemWeight ? (
                          <Typography>{`${t('weight')}: ${inventoryItem?.itemWeight}Lbs`}</Typography>
                        ) : (
                          <Typography>{`${t('weight')}: N/A`}</Typography>
                        )
                      }
                    />
                  </TabPanel>

                  <TabPanel value={2}>
                    <Typography fontSize={14}>
                      {inventoryItem?.userGuideFiles?.length > 0 ? t('filesInfo') : t('noFiles')}
                    </Typography>
                    <SkeletonWrapper
                      skeletonProps={{
                        height: 36,
                      }}
                      fullWidth
                      isLoading={gettingInventoryItem}
                      component={
                        inventoryItem && (
                          <Box sx={{ ml: 5, mt: 4 }}>
                            <ul>
                              {inventoryItem.userGuideFiles?.map(ugf => (
                                <li key={ugf.id}>
                                  <span
                                    onClick={() =>
                                      handleDownloadFile(getInventoryItemImage(inventoryItem.id, ugf.id), ugf[nameReference])
                                    }
                                    className={classes.fileLink}>
                                    <AttachFile color="secondary" fontSize="small" sx={{ marginBottom: '-0.3rem' }} />{' '}
                                    {ugf[nameReference]}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </Box>
                        )
                      }
                    />
                  </TabPanel>
                </TabContext>
              </Box>
            </CmtCardContent>
          </CmtCard>
        </GridItem>

        <GridItem xs={12}>
          <FrequentlyBoughtTogether
            entityId={inventoryItemId}
            frequentlyBoughtTogetherType={FrequentlyBoughtTogetherTypeEnum.Item}
          />
        </GridItem>
      </GridContainer>
    </Box>
  );
};

export default InventoryItemCategory;
